<script setup lang="ts">
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { useToast } from '~/components/ui/toast'
import { LucideLoaderCircle } from 'lucide-vue-next'
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { FeatureProfileLeadCancelDialog } from '~/components/feature/profile-lead-cancel-dialog'
import { getProfileLeadStatusClass } from '~/components/entity/profile-lead'
import type { TProfileLeadDetail } from '~/types/b2b'
import { getBidStatusClass } from '~/components/entity/b2b-lead'

const { t } = useI18n()
const { isLoggedIn, getToken } = useUser()
const { getPathWithLocale } = useLocalizedPath()

if (!isLoggedIn.value) {
  await navigateTo(getPathWithLocale('/login'))
}

definePageMeta({ robots: 'noindex' })

const route = useRoute()
const id = Number(route.params.id)
const { data: raw, error } = await useApi<{ data: TProfileLeadDetail }>(`/profile/leads/${id}`, {
  headers: { Authorization: `Bearer ${getToken()}` },
})

if (error.value || !raw.value?.data) {
  throw createError({ statusCode: 404 })
}

const lead = ref(raw.value.data)

const breadcrumbs = computed(() => [
  { title: t('page.profile.leads.detail.cabinet'), url: '/panel' },
  { title: t('page.profile.leads.breadcrumb'), url: '/profile/leads' },
  { title: t('page.profile.leads.detail.title', { id: lead.value.id }), url: `/profile/leads/${id}` },
])

const amountFormatter = new Intl.NumberFormat('ru-KG')

const formattedAmount = computed(() => {
  if (!lead.value.requested_amount) return '—'
  const num = parseFloat(lead.value.requested_amount)
  if (isNaN(num)) return lead.value.requested_amount
  return amountFormatter.format(num) + ' ' + lead.value.currency
})

const formattedDate = computed(() => {
  if (!lead.value.created_at) return '—'
  return useDateFormat(lead.value.created_at)
})

const formattedBidDates = computed(() =>
  Object.fromEntries(lead.value.bids.map((b) => [b.id, b.created_at ? useDateFormat(b.created_at) : null]))
)


const { $api } = useNuxtApp()
const { toast } = useToast()

const showCancelDialog = ref(false)
const canCancel = computed(() => lead.value.status === 'new' || lead.value.status === 'bank_responded')

const onCancelled = () => {
  lead.value.status = 'cancelled_by_user'
}

const acceptingBidId = ref<number | null>(null)

const canAcceptBid = computed(() => lead.value.status === 'bank_responded')

const organizationTitle = computed(() =>
  lead.value.is_common_pool
    ? t('page.profile.leads.all_organizations')
    : (lead.value.organization?.official_title ?? '—')
)

const acceptBid = async (bidId: number) => {
  acceptingBidId.value = bidId
  try {
    await $api(`/profile/leads/${id}/bids/${bidId}/accept`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
    })
    toast({ title: t('page.profile.leads.detail.accept_bid_success'), variant: 'success' })
    lead.value.status = 'accepted_by_user'
    for (const b of lead.value.bids) {
      b.status = b.id === bidId ? 'accepted' : 'rejected'
    }
  } catch (err: unknown) {
    const error = err as { data?: { message?: string } }
    toast({ title: error.data?.message ?? t('page.profile.leads.detail.accept_bid_error'), variant: 'destructive' })
  } finally {
    acceptingBidId.value = null
  }
}
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <div class="space-y-6" role="main">

        <div class="flex flex-wrap items-center gap-3">
          <Heading tag="h2">{{ $t('page.profile.leads.detail.title', { id: lead.id }) }}</Heading>
          <Badge :class="getProfileLeadStatusClass(lead.status)" variant="gray">
            {{ $t(`page.b2b.leads.lead_status.${lead.status}`) }}
          </Badge>
          <Button v-if="canCancel" variant="outline-gray" class="ml-auto" @click="showCancelDialog = true">
            {{ $t('page.b2b.leads.cancel_dialog.title') }}
          </Button>
        </div>

        <FeatureProfileLeadCancelDialog
          :lead-id="lead.id"
          :open="showCancelDialog"
          @close="showCancelDialog = false"
          @cancelled="onCancelled"
        />

        <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <ContentBlock variant="white">
            <h2 class="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
              {{ $t('page.profile.leads.detail.info_block') }}
            </h2>
            <div class="space-y-4">
              <div>
                <p class="text-xs text-gray-500">{{ $t('page.profile.leads.detail.field_organization') }}</p>
                <p class="mt-0.5 font-medium">{{ organizationTitle }}</p>
              </div>
              <hr class="border-gray-100" />
              <div>
                <p class="text-xs text-gray-500">{{ $t('page.profile.leads.detail.field_amount') }}</p>
                <p class="mt-0.5 text-xl font-bold">{{ formattedAmount }}</p>
              </div>
              <hr class="border-gray-100" />
              <div>
                <p class="text-xs text-gray-500">{{ $t('page.profile.leads.detail.field_date') }}</p>
                <p class="mt-0.5 font-medium">{{ formattedDate }}</p>
              </div>
              <hr v-if="lead.purpose" class="border-gray-100" />
              <div v-if="lead.purpose">
                <p class="text-xs text-gray-500">{{ $t('page.profile.leads.detail.field_purpose') }}</p>
                <p class="mt-0.5 font-medium">{{ lead.purpose }}</p>
              </div>
            </div>
          </ContentBlock>

          <ContentBlock variant="white">
            <h2 class="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
              {{ $t('page.profile.leads.detail.bids_block') }}
            </h2>
            <div v-if="!lead.bids.length" class="text-sm text-gray-500">
              {{ $t('page.profile.leads.detail.no_bids') }}
            </div>
            <div v-else class="space-y-4">
              <div
                v-for="(bid, index) in lead.bids"
                :key="bid.id"
              >
                <hr v-if="index > 0" class="mb-4 border-gray-100" />
                <p class="font-medium">{{ bid.organization.official_title }}</p>
                <Badge :class="getBidStatusClass(bid.status)" variant="gray" class="mt-1">
                  {{ $t(`page.b2b.leads.bid_status.${bid.status}`) }}
                </Badge>
                <div v-if="bid.offered_amount" class="mt-2 text-sm text-gray-700">
                  {{ $t('page.b2b.leads.bid') }}: <span class="font-medium">{{ bid.offered_amount }}%</span>
                </div>
                <p v-if="bid.details" class="mt-1 text-sm text-gray-600">{{ bid.details }}</p>
                <p v-if="formattedBidDates[bid.id]" class="mt-1 text-xs text-gray-500">{{ formattedBidDates[bid.id] }}</p>
                <Button
                  v-if="canAcceptBid"
                  size="sm"
                  class="mt-3"
                  :disabled="acceptingBidId !== null"
                  @click="acceptBid(bid.id)"
                >
                  <LucideLoaderCircle v-if="acceptingBidId === bid.id" class="mr-2 h-4 w-4 animate-spin" />
                  {{ $t('page.profile.leads.detail.accept_bid') }}
                </Button>
              </div>
            </div>
          </ContentBlock>
        </div>

      </div>
    </Container>
  </Main>
</template>
